// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React, { useCallback, useMemo, useState } from 'react'
import { array, bool, func, number, oneOf, string } from 'prop-types'
import { DataTable, SectionWrapper } from 'components'
import useTableSort from 'components/DataTable/hooks/useTableSort'
import columns from './columns'
import { initialTableSort } from './constants'
import NewComment from './NewComment'
import sortTableData from './helpers/sortTableData'
import { CommentWrapper } from './styles'
import useCanUserComment from './hooks/useCanUserComment'
import useSortedTableData from './hooks/useSortedTableData'

const Comments = props => {
  const {
    commentType,
    deleteAttachment,
    deleteComment,
    entityId,
    idRoot,
    isNewEntity,
    pathComment,
    requiredRole,
  } = props

  const [tableData, setTableData] = useState(props.tableData)

  const {
    assignSort,
    tableSort,
  } = useTableSort(initialTableSort)

  const sortedTableData = useSortedTableData(tableData, tableSort, sortTableData)

  const canUserComment = useCanUserComment(isNewEntity, pathComment, requiredRole)

  const createCallback = useCallback((
    ([newComment]) => setTableData(tableData => [...tableData, newComment])
  ), [setTableData])

  const customProps = useMemo(() => {
    // when a user deletes an attachment, we need to update the comment data.
    const updateSingleComment = updatedComment => setTableData(tableData => (
      tableData.map(d => (
        d[d.uniqueId] === updatedComment[updatedComment.uniqueId]
          ? updatedComment
          : d
      ))
    ))

    const removeComment = async commentId => {
      const { message } = await deleteComment(entityId, commentId)
      setTableData(tableData => (
        tableData.filter(d => d[d.uniqueId] !== commentId)
      ))
      return message
    }

    return {
      commentType,
      deleteAttachment,
      removeComment,
      updateSingleComment,
    }
  }, [commentType, deleteComment, deleteAttachment, entityId, setTableData])

  return (
    <CommentWrapper>
      <SectionWrapper
        label={'Comments & Attachments'}
        className={'width-100 margin-top-1 comments-section position-relative'}>
        <DataTable
          className={'bg-blue-dark table-layout-auto'}
          columns={columns}
          customProps={customProps}
          data={sortedTableData}
          handleSort={assignSort}
          idRoot={`${idRoot}-comments-table`}
          tableSort={tableSort} />
      </SectionWrapper>
      {
        canUserComment &&
        <NewComment
          createCallback={createCallback}
          idRoot={idRoot}
          pathComment={pathComment} />
      }
    </CommentWrapper>
  )
}

Comments.defaultProps = {
  requiredRole: null,
}

Comments.propTypes = {
  commentType: oneOf([
    'antennaSpec',
    'radio',
    'path',
  ]).isRequired,
  deleteAttachment: func.isRequired,
  deleteComment: func.isRequired,
  entityId: number,
  idRoot: string.isRequired,
  isNewEntity: bool.isRequired,
  pathComment: bool,
  requiredRole: string,
  tableData: array.isRequired,
}

export default Comments
